import { createContext, useState, useEffect, ReactNode } from "react";
import { secureStorage } from "@/infrastructure/storage";
import { STORAGE_KEYS } from "@/domain/constants/storage";
import { LOCALES } from "@/domain/constants/locales";
import i18n from "@/infrastructure/i18n";

/**
 * 語言上下文介面
 */
export interface LanguageContextType {
  toggleLanguage: () => void;
  changeLanguage: (language: string) => void;
}

/**
 * 語言上下文
 * 提供了應用程式的語言狀態和方法
 */
export const LanguageContext = createContext<LanguageContextType>({
  toggleLanguage: () => {},
  changeLanguage: () => {},
});

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * 語言提供者
 * 提供了應用程式的語言狀態和方法
 */
export function LanguageProvider({ children }: LanguageProviderProps) {
  // 取得初始語言
  const getInitialLanguage = () => {
    const savedLanguage = secureStorage.getItem(STORAGE_KEYS.LANGUAGE);
    return savedLanguage || LOCALES.DEFAULT;
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage);

  // 切換語言
  const toggleLanguage = () => {
    const newLang =
      currentLanguage === LOCALES.ZH_TW ? LOCALES.EN : LOCALES.ZH_TW;
    changeLanguage(newLang);
  };

  // 變更語言
  const changeLanguage = (lang: string) => {
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
    secureStorage.setItem(STORAGE_KEYS.LANGUAGE, lang);
  };

  // 監聽 i18n 語言變更事件
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  return (
    <LanguageContext.Provider value={{ toggleLanguage, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}
